/**
 * Donos e condutores de um veículo — o REGISTRO de propriedade. Cada linha é um vínculo com período:
 * a compra (`data_inicio`) e a venda (`data_fim`); em aberto = vínculo atual. Só pode haver UM
 * proprietário atual: ao registrar um novo dono vigente, o backend fecha o anterior na data da
 * compra (a venda) — o histórico de proprietários fica íntegro. O tipo (proprietário↔condutor↔
 * arrendatário) e as datas são editáveis aqui; a troca de dono também chega via importação do
 * DUT/ATPV-e (aba Documentos), que registra a transferência automaticamente.
 */
import { BadgeCheck, Car, Pencil, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ComboBuscaEntidade, type ConfigBusca, type ItemBusca } from '@/components/crud/ComboBuscaEntidade';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { Button } from '@/components/ui/button';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { formatar } from '@/lib/mascaras';

type Dono = {
  id: string;
  owner_tipo: string;
  owner: { tipo: string; id: string; rotulo: string };
  papel: string;
  data_inicio: string | null; // NULL = compra desconhecida (ex.: vendedor lido de um DUT)
  data_fim: string | null;
};

const PAPEIS = [
  { valor: 'proprietario', rotulo: 'Proprietário' },
  { valor: 'condutor', rotulo: 'Condutor' },
  { valor: 'arrendatario', rotulo: 'Arrendatário' },
];
const rotuloPapel = (p: string) => PAPEIS.find((x) => x.valor === p)?.rotulo ?? p;

const BUSCA: Record<'pessoa' | 'empresa', ConfigBusca> = {
  pessoa: {
    endpoint: '/cadastro/pessoas',
    rotulo: (p) => String(p.nome ?? ''),
    sub: (p) => (p.cpf ? formatar(String(p.cpf), 'cpf') : null),
    placeholder: 'Buscar pessoa por nome ou CPF…',
    camposRapido: [
      { chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true },
      { chave: 'cpf', rotulo: 'CPF', tipo: 'documento', formato: 'cpf' },
    ],
  },
  empresa: {
    endpoint: '/cadastro/empresas',
    rotulo: (e) => String(e.razao_social ?? e.nome_fantasia ?? ''),
    sub: (e) => (e.cnpj ? formatar(String(e.cnpj), 'cnpj') : null),
    placeholder: 'Buscar empresa por razão social ou CNPJ…',
    camposRapido: [
      { chave: 'razao_social', rotulo: 'Razão social', tipo: 'texto', obrigatorio: true },
      { chave: 'cnpj', rotulo: 'CNPJ', tipo: 'documento', formato: 'cnpj' },
    ],
  },
};

/** Data ISO (YYYY-MM-DD) → BR (DD/MM/YYYY). Vazio some. */
const dataBR = (iso?: string | null) => (iso ? iso.split('-').reverse().join('/') : '');

/** Período legível de propriedade: "10/01/2020 → 01/06/2023" · "desde 10/01/2020" · "até …". */
function periodo(inicio: string | null, fim: string | null): string {
  const i = dataBR(inicio);
  const f = dataBR(fim);
  if (i && f) return `${i} → ${f}`;
  if (i) return `desde ${i}`;
  if (f) return `até ${f}`;
  return '—';
}

export function PainelDonosVeiculo({ veiculoId }: { veiculoId: string }) {
  const base = '/cadastro/veiculos/vinculos';
  const url = `/cadastro/veiculos/${veiculoId}/vinculos`;
  const [donos, setDonos] = useState<Dono[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [editando, setEditando] = useState<string | null>(null);

  // Formulário de adição.
  const [tipoOwner, setTipoOwner] = useState<'pessoa' | 'empresa'>('pessoa');
  const [alvo, setAlvo] = useState<ItemBusca | null>(null);
  const [papel, setPapel] = useState('proprietario');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function carregar() {
    const r = await api.get<Dono[]>(url);
    setCarregando(false);
    if (r.ok) setDonos(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  function limparForm() {
    setAlvo(null);
    setPapel('proprietario');
    setInicio('');
    setFim('');
    setTipoOwner('pessoa');
  }

  async function adicionar(): Promise<boolean> {
    if (!alvo || salvando) return false;
    setSalvando(true);
    const r = await api.post(base, {
      veiculo_id: veiculoId,
      owner_tipo: tipoOwner,
      owner_id: alvo.id,
      papel,
      data_inicio: inicio || null,
      data_fim: fim || null,
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    const trocou = papel === 'proprietario' && !fim;
    toast.success(
      trocou
        ? 'Novo proprietário registrado — o dono anterior virou histórico.'
        : `${BUSCA[tipoOwner].rotulo(alvo)} vinculado como ${rotuloPapel(papel)}.`,
    );
    limparForm();
    void carregar();
    return true;
  }

  async function remover(d: Dono) {
    const r = await api.del(`${base}/${d.id}`);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Vínculo removido.');
    void carregar();
  }

  async function salvarEdicao(d: Dono, novoPapel: string, novoInicio: string, novoFim: string) {
    const r = await api.patch(`${base}/${d.id}`, {
      papel: novoPapel,
      data_inicio: novoInicio || null,
      data_fim: novoFim || null,
    });
    if (!r.ok) return void toast.error(r.erro);
    setEditando(null);
    toast.success('Vínculo atualizado.');
    void carregar();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Car className="size-4 text-accent" />
          Donos e condutores
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          O registro de propriedade do veículo, com data de compra e de venda. Só um proprietário
          fica atual (em aberto); ao registrar um novo dono, o anterior vira histórico.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estadoDaLista(carregando, donos.length === 0, 'Nenhum dono ou condutor ainda.') ?? (
          <div className="flex flex-col gap-2">
            {donos.map((d) =>
              editando === d.id ? (
                <FormEdicaoDono
                  key={d.id}
                  dono={d}
                  onCancelar={() => setEditando(null)}
                  onSalvar={(p, i, f) => void salvarEdicao(d, p, i, f)}
                />
              ) : (
                <div
                  key={d.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-[15px] text-text">{d.owner.rotulo}</span>
                    <span className="tipo-corpo-pequeno text-text-tertiary">
                      {d.owner.tipo === 'empresa' ? 'Empresa' : 'Pessoa'} · {periodo(d.data_inicio, d.data_fim)}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {!d.data_fim && d.papel === 'proprietario' && (
                      <Chip aceso>
                        <BadgeCheck className="size-3.5" />
                        Atual
                      </Chip>
                    )}
                    <Chip>{rotuloPapel(d.papel)}</Chip>
                    <Button variant="fantasma" size="sm" onClick={() => setEditando(d.id)} aria-label="Editar">
                      <Pencil />
                    </Button>
                    <Button
                      variant="fantasma"
                      size="sm"
                      className="text-danger hover:text-danger"
                      onClick={() => void remover(d)}
                      aria-label="Remover"
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
              ),
            )}
          </div>
        )}

        <DrawerAdicionar
          titulo="Novo dono ou condutor"
          descricao="Busque a pessoa/empresa (ou cadastre na hora), diga o papel e o período. Proprietário sem data de venda vira o dono atual."
          rotuloAbrir="Adicionar dono/condutor"
          rotuloSalvar="Vincular"
          podeSalvar={!!alvo}
          salvando={salvando}
          onSalvar={adicionar}
          onFechar={limparForm}
        >
          <div className="flex flex-col gap-2">
            <span className="tipo-rotulo">Quem</span>
            <div className="flex gap-1 rounded-md bg-surface-2 p-1">
              {(['pessoa', 'empresa'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setTipoOwner(t);
                    setAlvo(null);
                  }}
                  className={`flex-1 rounded-sm px-3 py-1.5 text-[13px] font-medium capitalize transition ${
                    tipoOwner === t ? 'bg-surface text-text shadow-[var(--shadow-sm)]' : 'text-text-tertiary'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <ComboBuscaEntidade key={tipoOwner} config={BUSCA[tipoOwner]} onSelecionar={setAlvo} />
          </div>
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">Papel</span>
            <select
              value={papel}
              onChange={(e) => setPapel(e.target.value)}
              className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
            >
              {PAPEIS.map((p) => (
                <option key={p.valor} value={p.valor}>
                  {p.rotulo}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Compra / início</span>
              <input
                type="date"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
                className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
              />
            </label>
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Venda / fim (vazio = atual)</span>
              <input
                type="date"
                value={fim}
                onChange={(e) => setFim(e.target.value)}
                className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
              />
            </label>
          </div>
          {papel === 'proprietario' && !fim && (
            <p className="tipo-corpo-pequeno text-text-tertiary">
              Sem data de venda, este vira o <strong className="text-text">proprietário atual</strong> — o dono
              vigente anterior é encerrado na data da compra.
            </p>
          )}
        </DrawerAdicionar>
      </CardContent>
    </Card>
  );
}

/** Edição in-place de um vínculo: papel + datas (o id é estável, então tudo é editável). */
function FormEdicaoDono({
  dono,
  onSalvar,
  onCancelar,
}: {
  dono: Dono;
  onSalvar: (papel: string, inicio: string, fim: string) => void;
  onCancelar: () => void;
}) {
  const [papel, setPapel] = useState(dono.papel);
  const [inicio, setInicio] = useState(dono.data_inicio ?? '');
  const [fim, setFim] = useState(dono.data_fim ?? '');
  const campo =
    'h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]';
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border-strong bg-surface-2 px-3 py-3">
      <span className="tipo-rotulo text-text-secondary">{dono.owner.rotulo}</span>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">Papel</span>
          <select value={papel} onChange={(e) => setPapel(e.target.value)} className={campo}>
            {PAPEIS.map((p) => (
              <option key={p.valor} value={p.valor}>
                {p.rotulo}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">Compra</span>
          <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className={campo} />
        </label>
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">Venda</span>
          <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className={campo} />
        </label>
        <Button type="button" variant="secundario" onClick={() => onSalvar(papel, inicio, fim)}>
          Salvar
        </Button>
        <Button type="button" variant="fantasma" onClick={onCancelar} aria-label="Cancelar">
          <X />
        </Button>
      </div>
    </div>
  );
}
